import {
  Component, OnInit, forwardRef, Input,  AfterViewInit, ElementRef,
  ChangeDetectorRef,  Output, EventEmitter,  Optional, OnDestroy
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR, ControlValueAccessor, AbstractControl, ControlContainer,
  FormGroup, FormControl
} from '@angular/forms';
import { TextFieldTypes } from '@ionic/core';
import { Subscription } from 'rxjs';

@Component({
    selector: 'viva-input',
    templateUrl: './viva-input.component.html',
    styleUrls: ['./viva-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => VivaInputComponent),
            multi: true
        }
    ],
    standalone: false
})
export class VivaInputComponent implements OnInit, AfterViewInit, ControlValueAccessor, OnDestroy {
  @Input() control: AbstractControl | null = null;
  @Input() label?: string;
  @Input() formControlName: string | number | null = null;
  @Input() placeholder?: string;
  @Input() type: TextFieldTypes = 'text';
  @Input() iconStartSrc?: string;
  @Input() iconEndSrc?: string;
  @Input() required?: string;
  @Input() maxlength?: string;
  @Input() minlength?: string;
  @Input() pattern?: string;
  @Input() inputmode?: string;
  @Input() autocomplete?: string;
  @Input() autocapitalize?: string;
  @Input() iconStartColor?: string = 'var(--ion-color-light)';
  @Input() iconEndColor?: string = 'var(--ion-color-light)';
  @Output() changeEvent = new EventEmitter<Event>();
  @Input() onkeydown?: (event: KeyboardEvent) => boolean;

  @Input() value: any = ''; // Add value as an Input
  @Output() valueChange = new EventEmitter<any>(); // Add valueChange as an Output

  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };
  private nativeElement: HTMLIonInputElement;
  private valueChangeSubscription?: Subscription;

  constructor(
    @Optional() private controlContainer: ControlContainer,
    private cdr: ChangeDetectorRef,
    private elRef: ElementRef
  ) {
    this.nativeElement = elRef.nativeElement as HTMLIonInputElement;
  }

  ngOnInit(): void {
    if (!this.control && this.formControlName !== null && this.controlContainer) {
      const formGroup = this.controlContainer.control as FormGroup;
      if (formGroup) {
        this.control = formGroup.get(String(this.formControlName)) as FormControl;
      }
    }

    if (this.control) {
      this.valueChangeSubscription = this.control.valueChanges.subscribe(() => {
        this.showHideNotch();
      });
    }
  }

  ngAfterViewInit(): void {
    this.showHideNotch();
  }

  ngOnDestroy(): void {
    if (this.valueChangeSubscription) {
      this.valueChangeSubscription.unsubscribe();
    }
  }

  writeValue(value: any): void {
    this.value = value;
    this.cdr.detectChanges();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.nativeElement.setAttribute('disabled', 'true');
    } else {
      this.nativeElement.removeAttribute('disabled');
    }
  }

  onInputChange(event: any) {
    const input = event.target as HTMLInputElement;
    let value = input.value; // Trim the value
    if (value) {
      value = value.trim();
    }

    if (this.maxlength) {
      const num = parseInt(this.maxlength);
      if (value.length > num) {
        value = value.slice(0, num); // Trim the value to the maximum length
        input.value = value; // Update the input value to the trimmed version
      }
    }

    this.value = value;
    this.valueChange.emit(this.value); // Emit valueChange event
    this.onChange(this.value);
    this.changeEvent.emit(event);
    this.showHideNotch();
  }

  onInputBlur(): void {
    this.onTouched();
    this.showHideNotch();
  }

  get isInvalid(): boolean {
    return this.control ? this.control.invalid && (this.control.dirty || this.control.touched) : false;
  }

  get labelText(): string | undefined {
    if (this.value == 0) {
      return this.label;
    }
    if (this.value == null || this.value == undefined || this.value == '') {
      return undefined;
    }
    return this.label;
  }

  get actualPlaceholder(): string | undefined {
    return this.placeholder || this.label;
  }

  showHideNotch(): void {
    const iconElement = this.nativeElement.querySelector('.input-outline-notch') as HTMLElement | null;
    if (iconElement) {
      if (this.value == 0) {
        iconElement.style.display = 'block';
      } else {
        if (this.value == null || this.value == undefined || this.value == '') {
          iconElement.style.display = 'none';
        } else {
          iconElement.style.display = 'block';
        }
      }
    } else {
      setTimeout(() => this.showHideNotch(), 500);
    }
  }
}
